import Auth0Identity from './identity';
import { logPrefix } from './constants';
import { UserIdentity } from './userIdentity';
import { transformAuth0Claims } from '../utils/transform';

/**
 * Makes a call to Auth0 `getTokenSilently()` to attempt a token refresh.
 *
 * @param refreshToken
 *
 * @returns
 * A __Promise__ with user info data. If id token is not returned, error thrown. If id token is returned, makes call to Auth0 `getIdTokenClaims()` to get a new access token. Updates `userIdentity`.
 * <details>
 * </summary>
 *
 * ```json
 * {
 *     "accessToken": "c7e9fed4bedf428b.a6941bbd21721cf7..."
 * }
 * ```
 * </summary>
 *
 * ```
 * See documentation for corresponding error codes:
 * https://auth0.github.io/auth0-spa-js/classes/auth0client.html
 *
 * ```
 * </details>
 *
 */

export async function extendSession(): Promise<UserIdentity> {
  if (Auth0Identity.userIdentity && Auth0Identity.userIdentity.accessToken) {
    try {
      await Auth0Identity.auth0.getTokenSilently();
    } catch (err) {
      throw err;
    }
  }
  const tokenInfo = await Auth0Identity.auth0.getIdTokenClaims();
  if (tokenInfo && tokenInfo.__raw) {
    const transformedAuth0Claims = transformAuth0Claims(tokenInfo);
    Auth0Identity.userIdentity = transformedAuth0Claims;
    return Auth0Identity.userIdentity;
  } else {
    Auth0Identity.clearSession();
    throw new Error(`${logPrefix} No refresh token found for this session`);
  }
}

/**
 * This method checks to see whether or not the user is currently authenticated by Auth0. If not, it calls extendSession() to get a new token. If user is logged in, a call is made to get a new access token. Updates `userIdentity`.
 *
 * @returns {(Promise<UserIdentity>)}
 * ```
 * See documentation for corresponding error codes:
 * https://auth0.github.io/auth0-spa-js/classes/auth0client.html
 *
 * ```
 */
export async function heartbeat(): Promise<UserIdentity> {
  const isLoggedIn = await Auth0Identity.isLoggedIn();
  if (isLoggedIn) {
    const tokenInfo = await Auth0Identity.auth0.getIdTokenClaims();
    if (tokenInfo && tokenInfo.__raw) {
      const transformedAuth0Claims = transformAuth0Claims(tokenInfo);
      Auth0Identity.userIdentity = transformedAuth0Claims;
      return Promise.resolve(Auth0Identity.userIdentity);
    } else {
      return Auth0Identity.extendSession();
    }
  } else {
    return Auth0Identity.extendSession();
  }
}
